import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { connect } from "react-redux"
import { useNavigate } from "react-router-dom"
import { saveAs } from "file-saver"
import { toast } from "react-toastify"
import { Dialog } from "@headlessui/react"
import { KButton, KDropdown, KInput, KSpan, KTextArea, KTitleSpan } from "kahuna-base-react-components"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"

import "./CoManager.css"
import InfringementCard from "./InfringementCard"
import { assistant, assistantStream, summaries } from "../../../apis"
import { currencyOptions, errorFilledToastOptions } from "../../../constants"
import PaymentForm from "../account/payment/PaymentForm"
import { getLanguageJsonFor, getSelectedLanguage, lang } from "../../../constants/languages"
import { RootState } from "../../../store"
import {
  CoManagerAttachment,
  CoManagerBankDetails,
  CoManagerClaimSupport,
  CoManagerCover,
  CoManagerMessage,
  CoManagerReleaseContext,
  CoManagerTrendingTrack,
  PaymentMethod,
  User
} from "../../../types"
import { formatIsoDate, formatNumber, formatTime, hashids, mapToCamelCase } from "../../../utility"

const NEW_RELEASE_ACTION = "new_release"
const PROMOTE_ACTION = "promote_track"
const COVER_ACTION = "generate_cover"
const DOWNLOAD_REPORT_ACTION = "download_report"
const PAYMENT_METHOD_ACTION = "payment_method"
const ROYALTIES_ACTION = "royalties"
const SONGTOOLS_ACTION = "songtools"
// The artist asked for a smart link or a pre-save themselves. No single page to open: a smart link
// belongs to one release, so the reply carries the list of their releases and they pick the one it is
// for. The promote action above is the other half of this, the rising track's link offered unasked.
const SMART_LINK_ACTION = "smart_link"
// Somebody else has released the artist's music. No page and no yes/no pair: the reply opens the
// report card, which is where the recording, the scan, the links and the document are settled.
const INFRINGEMENT_ACTION = "infringement"
const NEW_RELEASE_PATH = "/catalog/new-release"
// The pages the assistant can send the artist to. It only ever names the action, the route is ours.
const ROYALTIES_PATH = "/royalties"
// Where the post announcing a release is put together. It is Kahuna's, but it is not this app, so the
// button opens it in a tab of its own instead of routing to it.
const SONGTOOLS_URL = "https://kahuna.songtools.io/"
// How many drafts one request produces. Each one is a separate generation and a separate quota unit.
const COVER_VARIANTS = 2
const HISTORY_LIMIT = 10
// What the server reads text out of, and what it refuses. Checked here too so a picture that cannot be
// used is refused before it is uploaded rather than after.
// Mirrors SUPPORTED_IMAGE_TYPES on the server. A .jpg needs no entry of its own, it is image/jpeg like
// a .jpeg; heic and heif are what an iPhone photographs in.
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/avif", "image/heic", "image/heif"]
// What a file of each extension is, for a browser that hands one over with no type at all. Dragging a
// .heic out of Finder is where this happens: the format is newer than the type table some browsers
// ship, and without this the file is refused for having no type rather than for being the wrong one.
const IMAGE_TYPE_BY_EXTENSION: { [extension: string]: string } = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  avif: "image/avif",
  heic: "image/heic",
  heif: "image/heif"
}

const imageType = (file: File) =>
  file.type || IMAGE_TYPE_BY_EXTENSION[file.name.split(".").pop()?.toLowerCase() || ""] || ""
// Matches MAX_IMAGES on the server. Reading runs once per picture and it is the slow part of the
// reply, so past a handful the artist is waiting long enough to think nothing happened.
const MAX_IMAGES = 5
const MAX_IMAGE_BYTES = 5 * 1024 * 1024

// What may go on a support request beside the claim screenshots: the same pictures the chat takes, and
// a PDF, because a rights document usually is one. The server holds the same list.
const DOCUMENT_TYPES = [...IMAGE_TYPES, "application/pdf"]
const DOCUMENT_TYPE_BY_EXTENSION: { [extension: string]: string } = { ...IMAGE_TYPE_BY_EXTENSION, pdf: "application/pdf" }
const documentType = (file: File) =>
  file.type || DOCUMENT_TYPE_BY_EXTENSION[(file.name.split(".").pop() || "").toLowerCase()] || ""
// Five documents beside at most five screenshots, which is the ten files the endpoint accepts.
const MAX_DOCUMENTS = 5
// Higher than a chat picture: this is a scanned contract rather than a screenshot, and nothing reads
// it on the way through.
const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024
// Reveal speed of the assistant replies, in characters per second.
const TYPE_SPEED = 150
const LOADING_PLACEHOLDER = "—"

// Micro label styling shared by the cadence strip and the status chip.
const LABEL_PROPS = { fontSize: 11, lineHeight: "14px", fontWeight: 500, letterSpacing: "0.66px" }

const upper = (text: string) => text.toLocaleUpperCase(getSelectedLanguage())

/**
 * What identifies one account, so the card can find the account it sent to the form among the saved
 * ones. The number itself, out of whichever field the country keeps it in and without the spacing the
 * form writes it with.
 */
const accountKey = (method: PaymentMethod) =>
  (method.iban || method.accountNumber || "").replace(/\s+/g, "").toUpperCase()

// The assistant answers in the artist's language, which is not necessarily the language the interface
// is set to. Our own strings sit in the same conversation, so they follow the reply rather than the UI,
// in whichever language it came back in. A reply whose language could not be told keeps the interface's.
const wording = (locale?: string | null) => getLanguageJsonFor(locale).co_manager

const fill = (template: string, values: { [key: string]: string }) =>
  Object.entries(values).reduce((text, [key, value]) => text.split(`{${key}}`).join(value), template)

const monthsAgoText = (months: number) => {
  if (months <= 0) return lang.co_manager.this_month
  if (months === 1) return lang.co_manager.month_ago
  return fill(lang.co_manager.months_ago, { months: String(months) })
}

const buildGreeting = (context: CoManagerReleaseContext, name: string) => {
  const threshold = String(context.thresholdMonths)

  if (context.nextRelease) {
    return fill(lang.co_manager.greeting_upcoming, {
      name,
      title: context.nextRelease.title,
      date: formatIsoDate(context.nextRelease.releaseDate || "")
    })
  }

  if (!context.lastRelease) {
    return fill(lang.co_manager.greeting_no_release, { name })
  }

  const months = context.monthsSinceLastRelease ?? 0

  if (context.needsNewRelease) {
    return fill(lang.co_manager.greeting_overdue, {
      name,
      title: context.lastRelease.title,
      months: String(months),
      threshold
    })
  }

  return fill(lang.co_manager.greeting_healthy, {
    name,
    title: context.lastRelease.title,
    since: monthsAgoText(months),
    threshold
  })
}

const topTrendingTrack = (context: CoManagerReleaseContext | null) => context?.streams?.trendingTracks?.[0] || null

/**
 * The rising track message: what climbed, how much, where, and what to do about it while the climb
 * is still on.
 */
const buildRisingMessage = (track: CoManagerTrendingTrack) =>
  fill(lang.co_manager.greeting_rising, {
    title: track.title,
    percent: String(track.growthPercent),
    platform: track.platformLabel,
    previous: formatNumber(track.previousStreams),
    current: formatNumber(track.streams)
  })

/** Starter questions that match the artist's actual situation, not a fixed list. */
const suggestionsFor = (context: CoManagerReleaseContext | null) => {
  const sets = lang.co_manager.suggested_questions

  if (!context) return sets.healthy
  // A track climbing right now is the most time sensitive thing on the page, it goes first.
  if (topTrendingTrack(context)) return sets.rising
  if (context.nextRelease) return sets.upcoming
  if (!context.lastRelease) return sets.no_release
  if (context.needsNewRelease) return sets.overdue

  return sets.healthy
}

/**
 * Reveals text progressively so the artist reads it line by line instead of getting a wall at once.
 *
 * Only the opening messages need the animation, they are local strings that arrive complete. A streamed
 * reply reveals itself as its tokens land, so animate is off there and the caret follows the stream.
 */
const TypewriterText: FC<{
  text: string
  animate: boolean
  streaming?: boolean
  onTick: () => void
  onComplete: () => void
}> = ({ text, animate, streaming, onTick, onComplete }) => {
  const [revealed, setRevealed] = useState(animate ? "" : text)

  // Frame driven so it stays smooth, pauses with the tab and never renders the same slice twice.
  useEffect(() => {
    if (!animate) {
      setRevealed(text)
      // Streamed text grows under the caret, so the view has to follow it down.
      if (streaming) onTick()
      return
    }

    let frameId = 0
    let startedAt = 0
    let shown = 0

    const step = (now: number) => {
      if (!startedAt) startedAt = now
      const target = Math.min(text.length, Math.floor(((now - startedAt) / 1000) * TYPE_SPEED))

      if (target > shown) {
        shown = target
        setRevealed(text.slice(0, shown))
        onTick()
      }

      if (shown < text.length) {
        frameId = requestAnimationFrame(step)
      } else {
        onComplete()
      }
    }

    frameId = requestAnimationFrame(step)

    return () => cancelAnimationFrame(frameId)
  }, [text, animate])

  const lines = revealed.split("\n")
  const typing = streaming || (animate && revealed.length < text.length)

  return (
    <>
      {lines.map((line, index) => (
        <div className="co-manager-line" key={`line-${index}`}>
          <KSpan text={line || " "} color="#111" lineHeight="22px" />
          {typing && index === lines.length - 1 && <div className="co-manager-caret" />}
        </div>
      ))}
    </>
  )
}

const Metric: FC<{ label: string; value: string; meta?: string }> = ({ label, value, meta }) => (
  <div className="co-manager-metric">
    <KSpan text={upper(label)} color="#999" {...LABEL_PROPS} />
    <KSpan
      text={value}
      fontSize={16}
      lineHeight="22px"
      fontWeight={500}
      letterSpacing="-0.176px"
      color="#111"
      ellipsis
    />
    <div className="co-manager-metric-meta">
      {meta && <KSpan text={meta} fontSize={13} lineHeight="18px" color="#999" />}
    </div>
  </div>
)

const CadenceStatus: FC<{ context: CoManagerReleaseContext }> = ({ context }) => {
  const healthy = !context.needsNewRelease
  const rising = topTrendingTrack(context)

  return (
    <div className="co-manager-status-group">
      {rising && (
        <div className="co-manager-status">
          <img src="/account_icons/arrow-up-grey.svg" width={12} height={12} alt="rising" />
          <KSpan text={upper(lang.co_manager.status_rising)} color="#111" {...LABEL_PROPS} />
        </div>
      )}
      <div className="co-manager-status">
        <div className="co-manager-status-dot" style={{ backgroundColor: healthy ? "#8FDF01" : "#FF9558" }} />
        <KSpan
          text={upper(healthy ? lang.co_manager.status_healthy : lang.co_manager.status_overdue)}
          color="#111"
          {...LABEL_PROPS}
        />
      </div>
    </div>
  )
}

/**
 * Once the artist is in a conversation only the rising track stays: it is the one card that is about
 * right now rather than about the catalog, so it is the one worth keeping beside the thread. The other
 * three are openers and go with the starter chips.
 */
const CadenceStrip: FC<{ context: CoManagerReleaseContext | null; loading: boolean; risingOnly?: boolean }> = ({
  context,
  loading,
  risingOnly
}) => {
  const lastRelease = context?.lastRelease
  const nextRelease = context?.nextRelease
  const rising = topTrendingTrack(context)

  return (
    <div className="co-manager-metrics">
      <Metric
        label={lang.co_manager.rising_track}
        value={loading ? LOADING_PLACEHOLDER : rising?.title || lang.co_manager.no_rising_track}
        meta={
          !loading && rising
            ? fill(lang.co_manager.rising_meta, {
                percent: String(rising.growthPercent),
                platform: rising.platformLabel
              })
            : ""
        }
      />
      {!risingOnly && (
        <>
          <Metric
            label={lang.co_manager.last_release}
            value={loading ? LOADING_PLACEHOLDER : lastRelease?.title || lang.co_manager.no_release_yet}
            meta={
              !loading && lastRelease
                ? `${formatIsoDate(lastRelease.releaseDate || "")} · ${monthsAgoText(
                    context?.monthsSinceLastRelease ?? 0
                  )}`
                : ""
            }
          />
          <Metric
            label={lang.co_manager.upcoming_release}
            value={loading ? LOADING_PLACEHOLDER : nextRelease?.title || lang.co_manager.nothing_scheduled}
            meta={!loading && nextRelease ? formatIsoDate(nextRelease.releaseDate || "") : ""}
          />
          <Metric
            label={lang.co_manager.total_releases}
            value={loading ? LOADING_PLACEHOLDER : String(context?.releaseCount ?? 0)}
            meta={
              !loading && context
                ? fill(lang.co_manager.published_count, { count: String(context.releasedCount) })
                : ""
            }
          />
        </>
      )}
    </div>
  )
}

const CoManager: FC<{ user: User; paymentMethods: Record<string, PaymentMethod> }> = (props) => {
  const navigate = useNavigate()

  const [context, setContext] = useState<CoManagerReleaseContext | null>(null)
  const [contextLoading, setContextLoading] = useState(true)
  const [messages, setMessages] = useState<CoManagerMessage[]>([])
  // Opening messages waiting for the one before them to finish typing, so they land one by one.
  const [queuedMessages, setQueuedMessages] = useState<CoManagerMessage[]>([])
  const [input, setInput] = useState("")
  const [loading, setLoading] = useState(false)
  // Ids of the messages whose typewriter animation has finished, gates the CTA buttons.
  const [typedMessageIds, setTypedMessageIds] = useState<number[]>([])
  // The reporting month whose report is being built right now, so its button says so and cannot be
  // pressed twice: the PDF is generated per request and takes a moment.
  const [downloadingReport, setDownloadingReport] = useState<string | null>(null)
  // The picture waiting to go with the next message, as a data URI. Held here and nowhere else: it is
  // sent with the message and dropped, the server stores nothing either.
  const [attachments, setAttachments] = useState<CoManagerAttachment[]>([])
  // Whether a file is being dragged over the composer, so it can show it will take it. Counted rather
  // than flagged: dragging across a child element fires leave on the parent, and a boolean flickers.
  const [dragDepth, setDragDepth] = useState(0)
  // The account details read out of a picture, per message, while the artist looks them over. Editable
  // because OCR gets characters wrong and a bank field is the last place to live with that.
  const [bankForms, setBankForms] = useState<Record<number, CoManagerBankDetails>>({})
  // Messages whose form has been saved, so the card says so instead of offering to save it twice.
  const [savedBankForms, setSavedBankForms] = useState<number[]>([])
  // The payment methods form, opened over the conversation rather than as a page the artist is sent
  // to. It is where the country is asked, and the country is asked because it decides which fields an
  // account needs: a sort code in the UK, a routing number in the US, an IBAN here. The card carries
  // what was read off the picture into it, and the artist picks the country and checks the rest.
  // A message id when a card opened it, null when the artist asked for the form itself.
  const [paymentForm, setPaymentForm] = useState<{ messageId: number | null; method?: PaymentMethod } | null>(null)
  // The account a card sent to the form, waiting to turn up among the saved ones. The store takes the
  // row only once the request has come back, so the card says saved when the account really is saved.
  const [submittedMethod, setSubmittedMethod] = useState<{ messageId: number; method: PaymentMethod } | null>(null)
  // The picture being looked at full size. A thumbnail in the thread is too small to check an account
  // number against, which is the one thing an artist actually needs to do with it.
  const [openedImage, setOpenedImage] = useState<{ name: string; url: string } | null>(null)
  // The support request as the artist has it now, per message, from the moment they touch the draft.
  // Absent until then, so the card renders what the server drafted without a copy being made of it.
  const [claimDrafts, setClaimDrafts] = useState<Record<number, { subject: string; body: string }>>({})
  // The documents they put on that request. Never sent anywhere until the button on the card is pressed.
  const [claimDocuments, setClaimDocuments] = useState<Record<number, CoManagerAttachment[]>>({})
  const [sendingClaim, setSendingClaim] = useState<number | null>(null)
  const [sentClaims, setSentClaims] = useState<number[]>([])
  // Which card the document picker was opened from. One input serves every card on the page, so the
  // card has to be remembered between the click and the change event.
  const [documentTarget, setDocumentTarget] = useState<number | null>(null)
  // The release picked in a smart link list, per message. A thread can carry more than one of those
  // lists and each one answers its own question, so a pick belongs to the reply it was made under.
  const [smartLinkPicks, setSmartLinkPicks] = useState<Record<number, number>>({})

  const messagesRef = useRef<HTMLDivElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const documentInputRef = useRef<HTMLInputElement | null>(null)
  const messageIdRef = useRef(0)

  const nextMessageId = () => {
    messageIdRef.current += 1
    return messageIdRef.current
  }

  const scrollToBottom = useCallback(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight
    }
  }, [])

  const artistName = props.user.stageName || props.user.firstName || ""

  // Smart links are per release and hashid encoded, the same route the catalog opens.
  const smartLinkPathFor = (releaseId: number) => `/catalog/smart-link/${hashids.encode(String(releaseId))}`

  const smartLinkPath = useMemo(() => {
    const releaseId = topTrendingTrack(context)?.releaseId
    return releaseId ? smartLinkPathFor(releaseId) : null
  }, [context])

  // What the picker under a pre-save answer offers: scheduled releases first, because a pre-save is
  // only collected until release day and those are the ones on a clock.
  const smartLinkReleases = context?.smartLinkReleases || []

  const greet = useCallback(
    (releaseContext: CoManagerReleaseContext) => {
      setTypedMessageIds([])
      setMessages([
        {
          id: nextMessageId(),
          role: "assistant",
          text: buildGreeting(releaseContext, artistName),
          timeStamp: new Date(),
          animate: true,
          action: releaseContext.needsNewRelease ? NEW_RELEASE_ACTION : null,
          ctaState: "pending"
        }
      ])

      const rising = topTrendingTrack(releaseContext)
      // No button here on purpose: the advice is to share the song and, if they want, to run ads for it,
      // and neither of those is a page Kahuna can open for them.
      setQueuedMessages(
        rising
          ? [
              {
                id: nextMessageId(),
                role: "assistant",
                text: buildRisingMessage(rising),
                timeStamp: new Date(),
                animate: true
              }
            ]
          : []
      )
    },
    [artistName]
  )

  // Releases the next opening message once the current one has finished typing itself out.
  useEffect(() => {
    if (!queuedMessages.length) return

    const last = messages[messages.length - 1]
    const lastStillArriving =
      !!last && (last.loading || last.streaming || (last.animate && !typedMessageIds.includes(last.id)))
    if (lastStillArriving) return

    setMessages((previous) => [...previous, queuedMessages[0]])
    setQueuedMessages((previous) => previous.slice(1))
  }, [messages, queuedMessages, typedMessageIds])

  useEffect(() => {
    assistant("/co-manager/release-status/", "get")
      .then((response) => {
        const releaseContext = mapToCamelCase<CoManagerReleaseContext>(response.data)
        setContext(releaseContext)
        greet(releaseContext)
      })
      .catch(() => {
        toast.error(lang.co_manager.error, errorFilledToastOptions)
      })
      .finally(() => setContextLoading(false))
  }, [])

  // The card that sent an account to the form says saved once that account is among the saved ones.
  // Read off the store rather than off the press of the button: the form saves through the same store
  // action the payment methods page uses, whose reducer takes the row only on a request that came back.
  useEffect(() => {
    if (!submittedMethod) return

    const key = accountKey(submittedMethod.method)
    if (!key || !Object.values(props.paymentMethods).some((method) => accountKey(method) === key)) return

    setSavedBankForms((previous) => [...previous, submittedMethod.messageId])
    setSubmittedMethod(null)
  }, [props.paymentMethods, submittedMethod])

  useEffect(() => {
    scrollToBottom()
  }, [messages, scrollToBottom])

  const buildHistory = (currentMessages: CoManagerMessage[]) =>
    currentMessages
      .map((message) => ({ ...message, content: message.modelText || message.text }))
      .filter((message) => !message.loading && message.content)
      .slice(-HISTORY_LIMIT)
      .map((message) => ({ role: message.role, content: message.content }))

  const readImage = (file: File) =>
    new Promise<CoManagerAttachment>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        // A file the browser could not type reads back as "data:base64,...", which the server will not
        // take. The type worked out from the extension is written back in so it arrives labelled.
        const dataUrl = String(reader.result).replace(/^data:[^;,]*;?base64,/, `data:${imageType(file)};base64,`)
        resolve({ name: file.name, dataUrl })
      }
      reader.onerror = () => reject(new Error(file.name))
      reader.readAsDataURL(file)
    })

  /**
   * Reads picked or dropped images into data URIs so they can ride along in the message body.
   *
   * Refused here as well as on the server, because a 20 MB photo that is going to be rejected should
   * never be uploaded in the first place, and the artist should hear why straight away.
   *
   * A bad file among good ones is named and skipped rather than failing the lot: dropping a folder of
   * screenshots with a PDF in it should attach the screenshots and say what it left.
   */
  const attachImages = async (files?: FileList | File[] | null) => {
    const picked = Array.from(files || [])
    if (!picked.length) return

    const room = MAX_IMAGES - attachments.length
    if (room <= 0) {
      toast.error(fill(lang.co_manager.image_count_error, { count: String(MAX_IMAGES) }), errorFilledToastOptions)
      return
    }

    const usable = picked.filter((file) => {
      if (!IMAGE_TYPES.includes(imageType(file))) {
        toast.error(lang.co_manager.image_type_error, errorFilledToastOptions)
        return false
      }
      if (file.size > MAX_IMAGE_BYTES) {
        toast.error(lang.co_manager.image_size_error, errorFilledToastOptions)
        return false
      }
      return true
    })

    if (usable.length > room) {
      toast.error(fill(lang.co_manager.image_count_error, { count: String(MAX_IMAGES) }), errorFilledToastOptions)
    }

    const read = await Promise.all(
      usable.slice(0, room).map((file) => readImage(file).catch(() => null))
    )
    const done = read.filter((image): image is CoManagerAttachment => !!image)

    if (done.length < usable.slice(0, room).length) {
      toast.error(lang.co_manager.image_read_error, errorFilledToastOptions)
    }
    if (done.length) {
      setAttachments((previous) => [...previous, ...done].slice(0, MAX_IMAGES))
    }
  }

  /**
   * Whether what is being dragged is files, rather than a selection or a link from another tab.
   *
   * Read off dataTransfer.types, which is the only thing a browser exposes during a drag: the files
   * themselves are not readable until the drop, on purpose.
   */
  const draggingFiles = (event: React.DragEvent) =>
    Array.from(event.dataTransfer?.types || []).includes("Files")

  const readDocument = (file: File) =>
    new Promise<CoManagerAttachment>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const dataUrl = String(reader.result).replace(/^data:[^;,]*;?base64,/, `data:${documentType(file)};base64,`)
        resolve({ name: file.name, dataUrl })
      }
      reader.onerror = () => reject(new Error(file.name))
      reader.readAsDataURL(file)
    })

  /**
   * Reads the documents picked for one support request, the same way the chat reads a picture.
   *
   * Refused here as well as on the server for the same reason: a file that is going to be rejected
   * should not be uploaded first, and the artist should hear why while they are still looking at the
   * picker they opened.
   */
  const attachDocuments = async (messageId: number, files?: FileList | File[] | null, locale?: string | null) => {
    const words = wording(locale)
    const picked = Array.from(files || [])
    if (!picked.length) return

    const room = MAX_DOCUMENTS - (claimDocuments[messageId]?.length || 0)
    if (room <= 0) {
      toast.error(fill(words.claim_file_count_error, { count: String(MAX_DOCUMENTS) }), errorFilledToastOptions)
      return
    }

    const usable = picked.filter((file) => {
      if (!DOCUMENT_TYPES.includes(documentType(file))) {
        toast.error(words.claim_file_type_error, errorFilledToastOptions)
        return false
      }
      if (file.size > MAX_DOCUMENT_BYTES) {
        toast.error(words.claim_file_size_error, errorFilledToastOptions)
        return false
      }
      return true
    })

    if (usable.length > room) {
      toast.error(fill(words.claim_file_count_error, { count: String(MAX_DOCUMENTS) }), errorFilledToastOptions)
    }

    const read = await Promise.all(usable.slice(0, room).map((file) => readDocument(file).catch(() => null)))
    const done = read.filter((file): file is CoManagerAttachment => !!file)

    if (done.length < usable.slice(0, room).length) {
      toast.error(words.claim_file_read_error, errorFilledToastOptions)
    }
    if (done.length) {
      setClaimDocuments((previous) => ({
        ...previous,
        [messageId]: [...(previous[messageId] || []), ...done].slice(0, MAX_DOCUMENTS)
      }))
    }
  }

  /**
   * The claim pictures the request is about: the ones sent with the question this reply answers.
   *
   * They ride along to support with the request, because the screenshot is the evidence of what the
   * claim actually says, and support asking for it again is a round trip the artist should not pay
   * for. They sit on the question rather than on the answer, so it is the message before this one.
   */
  const claimScreenshots = (messageId: number) => {
    const answered = messages.filter((message) => message.id < messageId && message.role === "user")
    return answered[answered.length - 1]?.images || []
  }

  /**
   * Sends the request to Kahuna support, with the claim pictures and the documents on it.
   *
   * What goes is what is in the boxes, not what was drafted: the card is editable because the draft is
   * a starting point, and support has to read the artist's own account of it.
   */
  const sendClaimSupport = async (message: CoManagerMessage) => {
    const support = message.claimSupport as CoManagerClaimSupport
    const draft = claimDrafts[message.id] ?? { subject: support.subject, body: support.body }
    const documents = claimDocuments[message.id] || []
    const words = wording(message.locale)

    if (!draft.subject.trim() || !draft.body.trim()) {
      toast.error(words.claim_incomplete_error, errorFilledToastOptions)
      return
    }

    setSendingClaim(message.id)
    try {
      await assistant(
        "/co-manager/claim-support/",
        "POST",
        {
          subject: draft.subject,
          body: draft.body,
          kind: support.kind,
          files: [...claimScreenshots(message.id), ...documents].map((file) => ({
            name: file.name,
            data_url: file.dataUrl
          }))
        },
        {},
        // Not abortable: the requests share a controller per path, and this one is sent once and must
        // not be cancelled by whatever the artist types next.
        false
      )
      setSentClaims((previous) => [...previous, message.id])
      setMessages((previous) => [
        ...previous,
        {
          id: nextMessageId(),
          role: "assistant" as const,
          text: words.claim_sent_message,
          timeStamp: new Date(),
          locale: message.locale,
          animate: true
        }
      ])
    } catch (error) {
      toast.error(words.claim_send_error, errorFilledToastOptions)
    } finally {
      setSendingClaim(null)
    }
  }

  const sendMessage = async (text: string, images?: CoManagerAttachment[]) => {
    const sent = images || []
    const written = text.trim()
    // Pictures on their own still need something to ask the model. The artist did not write it, so it
    // goes to the model and to the history it reads, and never onto the screen as their words.
    const message = written || (sent.length ? lang.co_manager.image_default_question : "")
    if (!message || loading) return

    setLoading(true)
    setInput("")
    setAttachments([])
    // The artist took over the thread, an opening message that never got its turn is stale now.
    setQueuedMessages([])

    const history = buildHistory(messages)
    const userMessage: CoManagerMessage = {
      id: nextMessageId(),
      role: "user",
      text: written,
      modelText: message,
      timeStamp: new Date(),
      images: sent
    }
    const loadingMessage: CoManagerMessage = {
      id: nextMessageId(),
      role: "assistant",
      text: "",
      timeStamp: new Date(),
      loading: true
    }
    setMessages((previous) => [...previous, userMessage, loadingMessage])

    try {
      // Deltas land straight in the bubble, so the artist reads the reply while it is being written and
      // no typewriter has to fake it after the fact.
      const { action, releaseId, reportPeriod, bankDetails, claimSupport, locale } = await assistantStream(
        "/co-manager/message-stream/",
        { message, history, images: sent.map((image) => image.dataUrl) },
        (delta) =>
          setMessages((previous) =>
            previous.map((item) =>
              item.id === loadingMessage.id
                ? { ...item, text: item.text + delta, loading: false, streaming: true }
                : item
            )
          )
      )

      setMessages((previous) =>
        previous.map((item) =>
          item.id === loadingMessage.id
            ? {
                ...item,
                loading: false,
                streaming: false,
                action: action || null,
                releaseId: releaseId ?? null,
                reportPeriod: reportPeriod ?? null,
                bankDetails: bankDetails ?? null,
                claimSupport: claimSupport ?? null,
                infringement: action === INFRINGEMENT_ACTION,
                locale: locale ?? null,
                ctaState: "pending" as const
              }
            : item
        )
      )

      // Artwork needs no confirmation step: the assistant only emits the marker once the artist has
      // asked for a cover and settled which release, so a button here would be a second yes.
      if (action === COVER_ACTION && releaseId) generateCover(releaseId, locale)
    } catch (error) {
      toast.error(lang.co_manager.error, errorFilledToastOptions)
      setMessages((previous) =>
        previous.map((item) =>
          item.id === loadingMessage.id
            ? { ...item, text: lang.co_manager.error, loading: false, streaming: false, animate: false }
            : item
        )
      )
    } finally {
      setLoading(false)
    }
  }

  // Covers land in their own bubble: the requests outlive the reply by half a minute, so the artist
  // gets places to watch rather than a frozen chat. Two are generated at once and in parallel,
  // because what the artist is doing here is choosing a direction, and one image is not a choice.
  const generateCover = async (releaseId: number, locale?: string | null) => {
    const words = wording(locale)
    const placeholder: CoManagerMessage = {
      id: nextMessageId(),
      role: "assistant",
      text: words.cover_generating,
      timeStamp: new Date(),
      locale: locale ?? null,
      releaseId,
      generating: COVER_VARIANTS
    }
    setMessages((previous) => [...previous, placeholder])
    scrollToBottom()

    // Each variant is its own request so a slow or refused one does not hold up the rest, and each
    // lands in the grid the moment it is ready.
    const results = await Promise.all(
      Array.from({ length: COVER_VARIANTS }, async (_, variant) => {
        try {
          // The index picks the direction: photographic, then graphic. Without it both calls on one
          // prompt come back as two versions of the same picture.
          const response = await assistant(
            "/co-manager/generate-cover/", "POST", { release_id: releaseId, variant }, {}, false)
          const cover = mapToCamelCase<CoManagerCover>(response.data)
          setMessages((previous) =>
            previous.map((item) =>
              item.id === placeholder.id
                ? {
                    ...item,
                    text: "",
                    generating: Math.max(0, (item.generating ?? 1) - 1),
                    covers: [...(item.covers ?? []), { id: cover.id, imageUrl: cover.imageUrl }]
                  }
                : item
            )
          )
          scrollToBottom()
          return null
        } catch (error: any) {
          setMessages((previous) =>
            previous.map((item) =>
              item.id === placeholder.id ? { ...item, generating: Math.max(0, (item.generating ?? 1) - 1) } : item
            )
          )
          return error?.response?.data?.code ?? "generation_failed"
        }
      })
    )

    // Only say something went wrong when nothing came back; one draft is still a draft.
    const failures = results.filter(Boolean)
    if (failures.length === COVER_VARIANTS) {
      const quota = failures.includes("quota_exceeded")
      setMessages((previous) =>
        previous.map((item) =>
          item.id === placeholder.id
            ? { ...item, generating: 0, animate: true, text: quota ? words.cover_quota : words.cover_error }
            : item
        )
      )
    }
    scrollToBottom()
  }

  /**
   * Hands over the royalty summary of one reporting month, the same PDF the royalties page produces.
   *
   * The file is built per request and comes back as a blob, so it is saved rather than opened: the
   * point of offering it here is that the artist never has to leave the conversation for it.
   */
  const downloadReport = async (period: string, locale?: string | null) => {
    const words = wording(locale)
    setDownloadingReport(period)

    try {
      const response = await summaries(
        `/download_royalty_summary/?date=${period}&lang=${locale || getSelectedLanguage()}`,
        "get",
        null,
        { responseType: "blob" }
      )
      saveAs(response.data, `kahuna-royalty-summary-${period}.pdf`)
    } catch (error) {
      toast.error(words.report_error, errorFilledToastOptions)
    } finally {
      setDownloadingReport(null)
    }
  }

  const downloadCover = async (cover: CoManagerCover, locale?: string | null) => {
    try {
      // Through the API rather than the stored URL: that file is on another origin, so the browser
      // opens it in a tab instead of saving it.
      const response = await assistant(
        `/co-manager/covers/${cover.id}/download/`, "GET", null, { responseType: "blob" }, false)
      saveAs(response.data, `cover-${cover.id}.png`)
    } catch (error) {
      toast.error(wording(locale).cover_error, errorFilledToastOptions)
    }
  }

  /**
   * The button behind an action: what it says, where it goes and what the assistant answers either way.
   *
   * Returns null for a reply that carries no action, and for one whose page cannot be built for this
   * account, which is why the promote action is dropped when there is no smart link to open. A reply
   * that reaches here with an action we do not know renders no button rather than a dead one.
   */
  const ctaFor = (message: CoManagerMessage) => {
    // The reply's own language, not the interface's: the button sits inside the answer, and an English
    // button under a Turkish reply reads as a different voice talking.
    const words = wording(message.locale)

    switch (message.action) {
      case NEW_RELEASE_ACTION:
        return {
          label: words.cta_confirm,
          accepted: words.cta_accepted,
          dismissed: words.cta_dismissed,
          path: context?.newReleasePath || NEW_RELEASE_PATH
        }
      case PROMOTE_ACTION:
        return smartLinkPath
          ? {
              label: words.cta_promote,
              accepted: words.cta_promote_accepted,
              dismissed: words.cta_promote_dismissed,
              path: smartLinkPath
            }
          : null
      case PAYMENT_METHOD_ACTION:
        // The card filled in from a picture opens the same form, and a reply carrying both offers the
        // artist one thing twice. The card wins: it is the one that knows the account.
        if (message.bankDetails) return null

        return {
          label: words.cta_payment_method,
          accepted: words.cta_payment_method_accepted,
          dismissed: words.cta_payment_method_dismissed,
          // Over the conversation rather than away from it: the artist asked a question here, and
          // answering it should not cost them the thread they asked it in.
          modal: true as const
        }
      case ROYALTIES_ACTION:
        return {
          label: words.cta_royalties,
          accepted: words.cta_royalties_accepted,
          dismissed: words.cta_royalties_dismissed,
          path: ROYALTIES_PATH
        }
      case INFRINGEMENT_ACTION:
        // The card is the answer, and a confirmation button above it would only ask the artist to
        // agree to something already on their screen.
        return null
      case SMART_LINK_ACTION:
        // Same: the release list under the reply carries its own button, and which release it opens is
        // not something a yes answers.
        return null
      case SONGTOOLS_ACTION:
        return {
          label: words.cta_songtools,
          accepted: words.cta_songtools_accepted,
          dismissed: words.cta_songtools_dismissed,
          path: SONGTOOLS_URL,
          external: true
        }
      default:
        return null
    }
  }

  const answerCta = (message: CoManagerMessage, cta: NonNullable<ReturnType<typeof ctaFor>>, accepted: boolean) => {
    setMessages((previous) => [
      ...previous.map((item) =>
        item.id === message.id ? { ...item, ctaState: accepted ? ("accepted" as const) : ("dismissed" as const) } : item
      ),
      {
        id: nextMessageId(),
        role: "assistant" as const,
        text: accepted ? cta.accepted : cta.dismissed,
        timeStamp: new Date(),
        animate: true
      }
    ])

    if (!accepted) return

    if ("modal" in cta) {
      // Let the confirmation land before the form covers the conversation.
      setTimeout(() => setPaymentForm({ messageId: null }), 900)
      return
    }

    if ("external" in cta && cta.external) {
      // Opened here rather than after the confirmation, because a window.open outside the click is a
      // popup as far as the browser is concerned and gets blocked. The chat stays where it is.
      window.open(cta.path, "_blank", "noopener,noreferrer")
      return
    }

    // Let the confirmation land before leaving the page.
    setTimeout(() => navigate(cta.path), 900)
  }

  const renderCta = (message: CoManagerMessage) => {
    const cta = ctaFor(message)
    if (!cta) return null

    return (
      <div className="co-manager-bubble-actions">
        <KButton
          text={cta.label}
          onClick={() => answerCta(message, cta, true)}
          background="#111"
          textColor="#FFF"
          width="auto"
          height="34px"
          padding="7px 16px"
          borderRadius={100}
          fontSize={14}
        />
        <KButton
          text={wording(message.locale).cta_dismiss}
          onClick={() => answerCta(message, cta, false)}
          background="transparent"
          textColor="#666"
          width="auto"
          height="34px"
          padding="7px 12px"
          borderRadius={100}
          shadowDisabled
        />
      </div>
    )
  }

  /**
   * The account read out of a picture the artist sent, for them to look over.
   *
   * Read, never saved: the IBAN has passed its checksum on the server but every other field is a
   * reading of a photograph, and the artist is the only one who can say whether it matches the account
   * they meant. Which is also why the card does not save it. The button hands these values to the
   * payment methods form, where the artist picks the country - nothing in a photograph says which
   * country an account belongs to, and the country decides what else the account needs - and saves
   * from there, through the same form and the same checks as an account typed on the page.
   */
  const renderBankDetailsForm = (message: CoManagerMessage) => {
    const words = wording(message.locale)
    const details = bankForms[message.id] ?? (message.bankDetails as CoManagerBankDetails)
    const saved = savedBankForms.includes(message.id)
    // An account is already saved, so this card changes it rather than adding another one. The artist
    // pressing the button is the agreement to that, which is why the button says so and the line above
    // it names the account going away.
    const replacing = !!details.existingMethodId

    const update = (field: keyof CoManagerBankDetails, value: string) =>
      setBankForms((previous) => ({ ...previous, [message.id]: { ...details, [field]: value } }))

    const openPaymentForm = () =>
      setPaymentForm({
        messageId: message.id,
        method: {
          bankName: details.bankName.trim(),
          accountHolderName: details.accountHolderName.trim(),
          // In fours, the way the payment methods form writes an IBAN, so an account added from a
          // conversation reads the same as one typed on the page.
          iban: details.iban
            .replace(/\s+/g, "")
            .toUpperCase()
            .replace(/(.{4})/g, "$1 ")
            .trim(),
          swiftCode: details.swiftCode.trim().toUpperCase(),
          currency: details.currency,
          // Left blank on purpose, whatever the reading suggested: the country is the one thing on this
          // form the artist is the authority on, and the form asks them for it before anything else.
          countryType: "",
          // The account being replaced comes along, so the form changes that row instead of adding a
          // second one beside it. The artist agreed to the replacement by opening this card's button.
          ...(replacing ? { id: details.existingMethodId as number, user: props.user.id } : {})
        }
      })

    return (
      <div className="co-manager-bank-form">
        {!replacing && (
          <KSpan text={words.bank_form_title} fontSize={12} lineHeight="16px" fontWeight={500} color="#111" />
        )}
        {replacing && (
          <KSpan
            // Only some countries make the bank name mandatory on the payment methods form, so the
            // account being replaced may not have one to point at.
            text={
              details.existingBankName
                ? fill(words.bank_form_replacing, { bank: details.existingBankName })
                : words.bank_form_replacing_generic
            }
            fontSize={11}
            lineHeight="15px"
            color="#888"
          />
        )}
        <KInput
          value={details.bankName}
          placeholder={words.bank_form_bank}
          padding="10px"
          onChange={(value) => update("bankName", value)}
          disabled={saved}
        />
        <KInput
          value={details.accountHolderName}
          placeholder={words.bank_form_holder}
          padding="10px"
          onChange={(value) => update("accountHolderName", value)}
          disabled={saved}
        />
        <KInput
          value={details.iban}
          placeholder="IBAN"
          padding="10px"
          onChange={(value) => update("iban", value)}
          disabled={saved}
        />
        <div className="flex gap-2">
          <div style={{ width: "60%" }}>
            <KInput
              value={details.swiftCode}
              placeholder={words.bank_form_swift}
              padding="10px"
              onChange={(value) => update("swiftCode", value)}
              disabled={saved}
            />
          </div>
          <div style={{ width: "40%" }}>
            <KDropdown
              options={currencyOptions}
              selected={currencyOptions.find((option) => option.value2 === details.currency)}
              onSelect={(selected) => update("currency", (selected as KSelectOption)?.value2 || "")}
              placeholder={words.bank_form_currency}
            />
          </div>
        </div>
        {saved ? (
          <KSpan
            text={replacing ? words.bank_form_updated : words.bank_form_saved}
            fontSize={12}
            lineHeight="16px"
            color="#666"
          />
        ) : (
          <>
            <KSpan text={words.bank_form_check} fontSize={11} lineHeight="15px" color="#888" />
            <KButton
              dataTestId="co-manager-save-bank-details"
              text={replacing ? words.bank_form_update : words.bank_form_save}
              onClick={openPaymentForm}
              background="#111"
              textColor="#FFF"
              width="auto"
              height="34px"
              padding="7px 16px"
              borderRadius={100}
              fontSize={14}
            />
          </>
        )}
      </div>
    )
  }

  /**
   * The request to Kahuna support, drafted off the claim the artist sent a picture of.
   *
   * Drafted and not sent, which is the whole shape of this card: the claim details in it were read off
   * their screenshot and the account of the rights is theirs to make, so both boxes are editable and
   * nothing leaves until they press the button. The claim pictures go with it automatically, because
   * they are the evidence of what the claim says and support would only ask for them.
   *
   * A production claim cannot be disputed on an assertion, so the button waits for the documents.
   */
  const renderClaimSupportForm = (message: CoManagerMessage) => {
    const support = message.claimSupport as CoManagerClaimSupport
    const words = wording(message.locale)
    const draft = claimDrafts[message.id] ?? { subject: support.subject, body: support.body }
    const documents = claimDocuments[message.id] || []
    const screenshots = claimScreenshots(message.id)
    const sent = sentClaims.includes(message.id)
    const sending = sendingClaim === message.id
    const missingDocuments = support.documentsRequired && !documents.length

    const update = (field: "subject" | "body", value: string) =>
      setClaimDrafts((previous) => ({ ...previous, [message.id]: { ...draft, [field]: value } }))

    return (
      <div className="co-manager-claim-form">
        <KSpan text={words.claim_form_title} fontSize={12} lineHeight="16px" fontWeight={500} color="#111" />
        <KInput
          value={draft.subject}
          placeholder={words.claim_form_subject}
          padding="10px"
          onChange={(value) => update("subject", value)}
          disabled={sent || sending}
        />
        <KTextArea
          value={draft.body}
          placeholder={words.claim_form_body}
          padding="10px"
          onChange={(value) => update("body", value)}
          disabled={sent || sending}
          // No maxHeight on purpose: with one, KTextArea switches to growing on input and hides its
          // overflow, which clips a draft that arrives fifteen lines long and never scrolls to the
          // rest. Without it the box scrolls and the artist can drag it taller.
          height={240}
        />
        {!!screenshots.length && (
          <KSpan
            text={fill(words.claim_form_screenshots, { count: String(screenshots.length) })}
            fontSize={11}
            lineHeight="15px"
            color="#888"
          />
        )}
        {!!documents.length && (
          <div className="co-manager-claim-documents">
            {documents.map((file, index) => (
              <div className="co-manager-claim-document" key={`document-${message.id}-${index}-${file.name}`}>
                <KSpan text={file.name} fontSize={12} lineHeight="16px" color="#111" />
                {!sent && !sending && (
                  <KButton
                    dataTestId="co-manager-remove-document"
                    icon="/account_icons/close-line.svg"
                    onClick={() =>
                      setClaimDocuments((previous) => ({
                        ...previous,
                        [message.id]: (previous[message.id] || []).filter((_, at) => at !== index)
                      }))
                    }
                    background="transparent"
                    hoverBackground="#EFEFEF"
                    borderRadius={100}
                    shadowDisabled
                    width="20px"
                    height="20px"
                    padding="4px"
                  />
                )}
              </div>
            ))}
          </div>
        )}
        {sent ? (
          <KSpan text={words.claim_form_sent} fontSize={12} lineHeight="16px" color="#666" />
        ) : (
          <>
            <KSpan
              text={support.documentsRequired ? words.claim_form_documents : words.claim_form_documents_optional}
              fontSize={11}
              lineHeight="15px"
              color="#888"
            />
            <div className="co-manager-bubble-actions">
              <KButton
                dataTestId="co-manager-attach-document"
                text={words.claim_form_attach}
                leftIcon="/account_icons/attachment-line.svg"
                onClick={() => {
                  setDocumentTarget(message.id)
                  documentInputRef.current?.click()
                }}
                disabled={sending || documents.length >= MAX_DOCUMENTS}
                background="#FFF"
                hoverBackground="#FAFAFA"
                border="1px solid #E6E6E6"
                hoverBorder="1px solid #D6D6D6"
                textColor="#111"
                width="auto"
                height="34px"
                padding="7px 14px"
                gap="6px"
                borderRadius={100}
                fontSize={14}
                shadowDisabled
              />
              <KButton
                dataTestId="co-manager-send-claim-support"
                text={sending ? words.claim_form_sending : words.claim_form_send}
                onClick={() => sendClaimSupport(message)}
                disabled={sending || missingDocuments}
                background="#111"
                textColor="#FFF"
                width="auto"
                height="34px"
                padding="7px 16px"
                borderRadius={100}
                fontSize={14}
              />
            </div>
          </>
        )}
      </div>
    )
  }

  /**
   * The report button. No yes/no pair like the other actions: the artist asked for the file, so the
   * only thing left to do is hand it over.
   *
   * The white download icon rather than the plain one: download-line.svg is filled #111111, which is
   * this button's own background, so it drew the icon invisibly. The dark one stays right for the
   * light backgrounds the admin screens put it on.
   */
  const renderReportDownload = (message: CoManagerMessage) => {
    const words = wording(message.locale)
    const period = message.reportPeriod as string
    const downloading = downloadingReport === period

    return (
      <div className="co-manager-bubble-actions">
        <KButton
          dataTestId="co-manager-download-report"
          text={downloading ? words.report_downloading : words.report_download}
          leftIcon="/account_icons/download-line-white.svg"
          onClick={() => downloadReport(period, message.locale)}
          disabled={downloading}
          background="#111"
          textColor="#FFF"
          width="auto"
          height="34px"
          padding="7px 16px"
          gap="6px"
          borderRadius={100}
          fontSize={14}
        />
      </div>
    )
  }

  /**
   * The release list under a smart link or pre-save answer.
   *
   * A smart link belongs to one release, and which one it is for is the artist's to say: the assistant
   * is given five titles where they have a whole catalogue, so it asks with this list rather than by
   * guessing a name. The button opens that release's smart link page, which is also where the link is
   * created when it does not exist yet, so the same list answers "make me one" and "open mine".
   *
   * Nothing is rendered when the account has no release a link could be opened for. The assistant is
   * told to send those artists to the new release page instead, and a list of none under the answer
   * would be a question with no answers in it.
   */
  const renderSmartLinkPicker = (message: CoManagerMessage) => {
    const words = wording(message.locale)
    if (!smartLinkReleases.length) return null

    const options: KSelectOption[] = smartLinkReleases.map((release) => ({
      // The date rides along on a scheduled release and on nothing else: it is what says the link is
      // still collecting pre-saves, and on one already out it is only noise beside the title.
      label: release.upcoming
        ? fill(words.smart_link_upcoming_option, {
            title: release.title,
            date: formatIsoDate(release.releaseDate || "")
          })
        : release.title,
      value: release.id
    }))
    const release = smartLinkReleases.find((item) => item.id === smartLinkPicks[message.id]) || smartLinkReleases[0]

    return (
      <div className="co-manager-smart-link">
        <KDropdown
          dataTestId="co-manager-smart-link-release"
          options={options}
          selected={options.find((option) => option.value === release.id)}
          onSelect={(selected) => {
            const picked = (selected as KSelectOption)?.value
            if (picked) setSmartLinkPicks((previous) => ({ ...previous, [message.id]: picked }))
          }}
          placeholder={words.smart_link_placeholder}
        />
        <KButton
          dataTestId="co-manager-smart-link-open"
          text={release.upcoming ? words.smart_link_open_pre_save : words.smart_link_open}
          onClick={() => navigate(smartLinkPathFor(release.id))}
          background="#111"
          textColor="#FFF"
          width="auto"
          height="34px"
          padding="7px 16px"
          borderRadius={100}
          fontSize={14}
        />
      </div>
    )
  }

  const renderAssistantMessage = (message: CoManagerMessage) => (
    <div className="flex flex-row items-start justify-start gap-3" key={`message-${message.id}`}>
      <div className="co-manager-message-avatar">
        <img src="/account_icons/sparkle-white.svg" width={16} height={16} alt="co-manager" />
      </div>
      <div className="flex flex-col gap-1 items-start">
        {message.loading ? (
          <div className="co-manager-bubble">
            <img src="/account_icons/loading-3-dots.svg" style={{ width: 30 }} alt="loading" />
          </div>
        ) : message.generating || message.covers?.length ? (
          <>
            <div className="co-manager-bubble co-manager-cover-bubble">
              <div className="co-manager-cover-grid">
                {(message.covers ?? []).map((cover) => (
                  <div className="co-manager-cover-item" key={`cover-${cover.id}`}>
                    <img
                      className="co-manager-cover"
                      src={cover.imageUrl}
                      alt={wording(message.locale).cover_caption}
                      onLoad={scrollToBottom}
                    />
                    <div className="co-manager-cover-actions">
                      <button
                        type="button"
                        className="co-manager-cover-action"
                        onClick={() => downloadCover(cover, message.locale)}
                      >
                        <KSpan
                          text={wording(message.locale).cover_download}
                          fontSize={12}
                          lineHeight="16px"
                          color="#111"
                        />
                      </button>
                    </div>
                  </div>
                ))}
                {Array.from({ length: message.generating ?? 0 }, (_, index) => (
                  <div className="co-manager-cover-placeholder" key={`pending-${index}`}>
                    <img src="/account_icons/loading-3-dots.svg" style={{ width: 24 }} alt="loading" />
                  </div>
                ))}
              </div>
              <KSpan text={wording(message.locale).cover_caption} fontSize={12} lineHeight="16px" color="#777" />
              {/* The credit sits on the card, never on the image: a cover carrying a distributor's mark
                  is refused by the stores and is useless to the designer the artist takes it to. */}
              <div className="co-manager-cover-credit">
                <img src="/account_icons/logo.svg" width={16} height={16} alt="Kahuna" />
                <KSpan text={wording(message.locale).cover_credit} fontSize={11} lineHeight="14px" color="#999" />
              </div>
            </div>
            <div className="px-4">
              <KSpan text={formatTime(message.timeStamp)} fontSize={12} lineHeight="16px" color="#B2B2B2" />
            </div>
          </>
        ) : (
          <>
            <div className="co-manager-bubble">
              <TypewriterText
                text={message.text}
                animate={!!message.animate}
                streaming={!!message.streaming}
                onTick={scrollToBottom}
                onComplete={() => setTypedMessageIds((previous) => [...previous, message.id])}
              />
              {message.ctaState === "pending" &&
                !message.streaming &&
                (!message.animate || typedMessageIds.includes(message.id)) &&
                renderCta(message)}
              {message.action === DOWNLOAD_REPORT_ACTION &&
                message.reportPeriod &&
                !message.streaming &&
                (!message.animate || typedMessageIds.includes(message.id)) &&
                renderReportDownload(message)}
              {message.action === SMART_LINK_ACTION &&
                !message.streaming &&
                (!message.animate || typedMessageIds.includes(message.id)) &&
                renderSmartLinkPicker(message)}
              {message.bankDetails &&
                !message.streaming &&
                (!message.animate || typedMessageIds.includes(message.id)) &&
                renderBankDetailsForm(message)}
              {message.claimSupport &&
                !message.streaming &&
                (!message.animate || typedMessageIds.includes(message.id)) &&
                renderClaimSupportForm(message)}
              {message.infringement &&
                !message.streaming &&
                (!message.animate || typedMessageIds.includes(message.id)) && (
                  <InfringementCard
                    words={wording(message.locale)}
                    onSent={() =>
                      setMessages((previous) => [
                        ...previous,
                        {
                          id: nextMessageId(),
                          role: "assistant" as const,
                          text: wording(message.locale).infringement_sent_message,
                          timeStamp: new Date(),
                          locale: message.locale,
                          animate: true
                        }
                      ])
                    }
                  />
                )}
            </div>
            <div className="px-4">
              <KSpan text={formatTime(message.timeStamp)} fontSize={12} lineHeight="16px" color="#B2B2B2" />
            </div>
          </>
        )}
      </div>
    </div>
  )

  const renderUserMessage = (message: CoManagerMessage) => (
    <div className="flex flex-row items-start justify-end gap-3" key={`message-${message.id}`}>
      <div className="flex flex-col gap-1 items-end">
        {/* Above the question rather than inside the bubble: the picture is what the question is about,
            and a white bubble around it would box a photo in a colour it does not belong to. */}
        {!!message.images?.length && (
          <div className="co-manager-message-images">
            {message.images.map((image, index) => (
              <img
                key={`message-${message.id}-image-${index}`}
                className="co-manager-message-image"
                src={image.dataUrl}
                alt=""
                onLoad={scrollToBottom}
                onClick={() => setOpenedImage({ name: image.name, url: image.dataUrl })}
              />
            ))}
          </div>
        )}
        {!!message.text && (
          <div className="co-manager-bubble-user">
            <KSpan text={message.text} color="#FFF" lineHeight="22px" />
          </div>
        )}
        <div className="px-4">
          <KSpan text={formatTime(message.timeStamp)} fontSize={12} lineHeight="16px" color="#B2B2B2" />
        </div>
      </div>
      {props.user.firstName ? (
        <div className="co-manager-user-initial">
          <KSpan text={upper(props.user.firstName[0])} fontWeight={500} color="#111" />
        </div>
      ) : (
        <img src="/account_icons/avatar.svg" width={32} height={32} alt="avatar" />
      )}
    </div>
  )

  const renderSuggestions = () => (
    <div className="co-manager-suggestions">
      {suggestionsFor(context).map((question: string, index: number) => (
        <KButton
          key={`suggestion-${index}`}
          text={question}
          onClick={() => sendMessage(question)}
          background="#FFF"
          hoverBackground="#FAFAFA"
          border="1px solid #EAEAEA"
          hoverBorder="1px solid #D6D6D6"
          borderRadius={100}
          textColor="#666"
          fontSize={13}
          fontWeight={400}
          width="auto"
          height="32px"
          padding="7px 14px"
          shadowDisabled
        />
      ))}
    </div>
  )

  const sendDisabled = loading || (!input.trim() && !attachments.length)
  // A card naming an account to replace opens the form on that row, everything else adds a new one.
  const replacingInForm = !!paymentForm?.method?.id
  // The first thing the artist asks turns the page into a conversation. The starter chips and the
  // catalog cards are openers and go at that point, leaving the thread the console and the rising
  // track, which is the one card still worth reading while they talk.
  const started = messages.some((message) => message.role === "user")
  // The starters stay up until the artist writes something, and stay down while an opening message is queued.
  const showSuggestions = !loading && !queuedMessages.length && !started

  return (
    <div
      className="co-manager-page"
      /* The drop lands anywhere on the page. An artist dragging a screenshot out of Finder aims at
         the conversation, which is the big thing in front of them, not at the one row at the bottom
         of it, and a drop that misses is a file opened in a new tab and a lost place in the chat. */
      onDragEnter={(event) => {
        if (!draggingFiles(event)) return
        event.preventDefault()
        if (!loading) setDragDepth((depth) => depth + 1)
      }}
      onDragOver={(event) => {
        // Without this the browser refuses the drop and opens the file instead.
        if (draggingFiles(event)) event.preventDefault()
      }}
      onDragLeave={(event) => {
        if (!draggingFiles(event)) return
        // Counted rather than toggled, because dragging across the page crosses every element in it
        // and each crossing fires a leave. A leave with no related target is the cursor going out of
        // the window, where no further leave is coming, so the count is dropped rather than lowered.
        setDragDepth((depth) => (event.relatedTarget ? Math.max(0, depth - 1) : 0))
      }}
      onDrop={(event) => {
        if (!draggingFiles(event)) return
        event.preventDefault()
        setDragDepth(0)
        if (!loading) attachImages(event.dataTransfer.files)
      }}
    >
      <div className="co-manager-header">
        <div className="flex flex-row items-center gap-3">
          <div className="co-manager-avatar">
            <img src="/account_icons/sparkle-white.svg" width={20} height={20} alt="co-manager" />
          </div>
          <div className="flex flex-col">
            <KTitleSpan text={lang.co_manager.title} fontSize={24} lineHeight="32px" color="#111" />
            <KSpan text={lang.co_manager.subtitle} fontSize={14} lineHeight="20px" color="#666" />
          </div>
        </div>
        <div className="co-manager-header-actions">
          {!contextLoading && context && <CadenceStatus context={context} />}
          <KButton
            text={lang.co_manager.reset}
            leftIcon="/account_icons/plus.svg"
            onClick={() => context && greet(context)}
            disabled={!context || loading}
            background="#FFF"
            hoverBackground="#FAFAFA"
            border="1px solid #E6E6E6"
            hoverBorder="1px solid #D6D6D6"
            borderRadius={100}
            textColor="#111"
            width="auto"
            height="36px"
            padding="8px 14px"
            gap="6px"
            shadowDisabled
          />
        </div>
      </div>

      <div className="co-manager-console">
        <CadenceStrip context={context} loading={contextLoading} risingOnly={started} />
        <div className="co-manager-messages" ref={messagesRef}>
          <div className="co-manager-thread">
            {messages.map((message) =>
              message.role === "assistant" ? renderAssistantMessage(message) : renderUserMessage(message)
            )}
          </div>
        </div>
        {showSuggestions && renderSuggestions()}
        {/* The page takes the drop; the composer only shows where the file is going to end up, which
            is the field the question gets typed into. */}
        <div className={`co-manager-composer${dragDepth ? " co-manager-composer-dropping" : ""}`}>
          {!!attachments.length && (
            <div className="co-manager-attachments">
              {attachments.map((image, index) => (
                <div className="co-manager-attachment" key={`attachment-${index}-${image.name}`}>
                  <img className="co-manager-attachment-thumb" src={image.dataUrl} alt={image.name} />
                  {/* Only on hover: the thumbnail says what it is, and a row of buttons sitting on top
                      of them permanently is the loudest thing in a composer that is otherwise quiet. */}
                  <div className="co-manager-attachment-remove">
                    <KButton
                      dataTestId="co-manager-remove-attachment"
                      icon="/account_icons/close-line.svg"
                      onClick={() => setAttachments((previous) => previous.filter((_, at) => at !== index))}
                      background="#111"
                      hoverBackground="#333"
                      borderRadius={100}
                      shadowDisabled
                      width="20px"
                      height="20px"
                      padding="4px"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          <div
            className="co-manager-composer-field"
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault()
                sendMessage(input, attachments)
              }
            }}
          >
            {/* The documents a support request carries. Its own input rather than the one beside it,
                because it takes PDFs the chat does not, and it fills whichever card was pressed. */}
            <input
              ref={documentInputRef}
              type="file"
              accept={DOCUMENT_TYPES.join(",")}
              className="hidden"
              multiple
              onChange={(event) => {
                const target = documentTarget
                const locale = messages.find((message) => message.id === target)?.locale
                if (target) attachDocuments(target, event.target.files, locale)
                // Cleared so picking the same file twice in a row still fires a change.
                event.target.value = ""
              }}
            />
            {/* The image input the composer opens: KButton clicks it, so nothing here is a bare control. */}
            <input
              ref={fileInputRef}
              type="file"
              accept={IMAGE_TYPES.join(",")}
              className="hidden"
              multiple
              onChange={(event) => {
                attachImages(event.target.files)
                // Cleared so picking the same file twice in a row still fires a change.
                event.target.value = ""
              }}
            />
            <KButton
              dataTestId="co-manager-attach-image"
              icon="/account_icons/attachment-line.svg"
              onClick={() => fileInputRef.current?.click()}
              disabled={loading || attachments.length >= MAX_IMAGES}
              background="transparent"
              shadowDisabled
              width="34px"
              height="34px"
              padding="7px"
            />
            <div className="grow px-3">
              <KTextArea
                value={input}
                onChange={(value) => setInput(value)}
                placeholder={lang.co_manager.input_placeholder}
                padding="7px 0px"
                background="transparent"
                activeBackground="transparent"
                hoverBackground="transparent"
                shadowDisabled
                maxHeight={120}
                height={20}
                clearTextOnPressedEnter
              />
            </div>
            <KButton
              icon={sendDisabled ? "/account_icons/arrow-up-grey.svg" : "/account_icons/arrow-up-white.svg"}
              onClick={() => sendMessage(input, attachments)}
              disabled={sendDisabled}
              background="#111"
              hoverBackground="#333"
              borderRadius={100}
              width="34px"
              height="34px"
              padding="7px"
              shadowDisabled
            />
          </div>
          <div className="flex justify-center">
            <KSpan text={lang.co_manager.powered_by} fontSize={12} lineHeight="16px" color="#B2B2B2" />
          </div>
        </div>
      </div>

      {/* What is about to happen, over the conversation while a file is being dragged across it. The
          page has no visible edge of its own to light up, so without this the only feedback is a
          field at the bottom changing colour, which is nothing at the top of a long thread. */}
      {!!dragDepth && (
        <div className="co-manager-drop-veil">
          <div className="co-manager-drop-hint">
            <img src="/account_icons/attachment-line.svg" width={20} height={20} alt="" />
            <KSpan text={lang.co_manager.drop_hint} fontWeight={500} lineHeight="20px" color="#111" />
          </div>
        </div>
      )}

      {/* The payment methods form, over the conversation instead of in place of it. It opens on the
          country step, which is the whole reason for opening it: the card hands over what was read off
          the picture, the artist says which country the account is in, and the form then asks for what
          that country actually needs. Saving is the form's own, so an account added from a conversation
          goes through the same checks as one added on the payment methods page. */}
      <Dialog open={!!paymentForm} onClose={() => setPaymentForm(null)}>
        <div className="fixed top-0 z-200 left-0 w-screen h-screen flex items-center justify-center bg-[#0000004d]">
          <PaymentForm
            title={
              replacingInForm
                ? lang.account.payment_methods.payment_form.edit_bank_account
                : lang.account.payment_methods.payment_form.add_bank_account
            }
            initialValues={paymentForm?.method}
            {...(replacingInForm
              ? { edit: true, setEdit: () => setPaymentForm(null) }
              : { addPaymentMethod: true, setAddPaymentMethod: () => setPaymentForm(null) })}
            onSubmit={(method) => {
              if (paymentForm?.messageId) setSubmittedMethod({ messageId: paymentForm.messageId, method })
            }}
          />
        </div>
      </Dialog>

      {/* The picture full size, over a dark backdrop so nothing of the thread competes with it. What
          the artist came here to do is read an account number off their own screenshot, and the
          thumbnail in the thread is far too small for that. */}
      <Dialog open={!!openedImage} onClose={() => setOpenedImage(null)}>
        <div className="co-manager-image-viewer" onClick={() => setOpenedImage(null)}>
          {/* Stops a click on the picture itself from closing it, while a click on the dark around it
              still does. */}
          <div className="co-manager-image-viewer-panel" onClick={(event) => event.stopPropagation()}>
            <div className="co-manager-image-viewer-bar">
              <KSpan text={openedImage?.name || ""} color="#FFF" fontWeight={500} lineHeight="20px" />
              <KButton
                dataTestId="co-manager-close-image"
                icon="/account_icons/close-line.svg"
                onClick={() => setOpenedImage(null)}
                background="transparent"
                hoverBackground="#FFFFFF1A"
                borderRadius={100}
                width="32px"
                height="32px"
                padding="6px"
                shadowDisabled
              />
            </div>
            <img className="co-manager-image-viewer-image" src={openedImage?.url} alt={openedImage?.name || ""} />
          </div>
        </div>
      </Dialog>
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return { user: state.auth.user, paymentMethods: state.paymentMethods }
}

export default connect(mapStateToProps)(CoManager)
